import React from 'react';
import { motion } from 'framer-motion';
/**
 * MindmapVisual — STU-MINDMAP visual aid
 *
 * Renders a radial mindmap with:
 *   - 1 central node (hoofdonderwerp)
 *   - N branches (hoofdtakken), evenly distributed around the center
 *   - Each branch can have leaves (details), shown as smaller pills
 *
 * Each branch gets its own color (cycling through a palette) — een kerntechniek
 * uit Buzan's mindmap-methode die kinderen helpt structuur te zien.
 *
 * Data shape:
 * {
 *   topic: string           // text van center
 *   branches: Array<{
 *     label: string         // kernwoord op de hoofdtak
 *     leaves?: string[]     // optionele details
 *     highlight?: boolean
 *   }>
 *   highlightCenter?: boolean
 *   showLeaves?: boolean    // default true
 *   label?: string
 *   mode?: 'stimulus' | 'support'
 * }
 */
interface MindmapBranch {
  label: string;
  leaves?: string[];
  highlight?: boolean;
}
interface MindmapVisualProps {
  topic?: string;
  branches?: MindmapBranch[];
  highlightCenter?: boolean;
  showLeaves?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
const BRANCH_PALETTE = [{
  stroke: '#ef4444',
  fill: '#fee2e2',
  text: '#7f1d1d',
  dasharray: 'none'
}, {
  stroke: '#f59e0b',
  fill: '#fef3c7',
  text: '#78350f',
  dasharray: 'none'
}, {
  stroke: '#10b981',
  fill: '#d1fae5',
  text: '#064e3b',
  dasharray: 'none'
}, {
  stroke: '#3b82f6',
  fill: '#dbeafe',
  text: '#1e3a8a',
  dasharray: 'none'
}, {
  stroke: '#8b5cf6',
  fill: '#ede9fe',
  text: '#4c1d95',
  dasharray: 'none'
}, {
  stroke: '#ec4899',
  fill: '#fce7f3',
  text: '#831843',
  dasharray: 'none'
},
// Extended palette for >= 6 branches
{
  stroke: '#ef4444',
  fill: '#ffffff',
  text: '#7f1d1d',
  dasharray: '4 4'
}, {
  stroke: '#f59e0b',
  fill: '#ffffff',
  text: '#78350f',
  dasharray: '4 4'
}, {
  stroke: '#10b981',
  fill: '#ffffff',
  text: '#064e3b',
  dasharray: '4 4'
}, {
  stroke: '#3b82f6',
  fill: '#ffffff',
  text: '#1e3a8a',
  dasharray: '4 4'
}];
const CENTER_R = 56; // radius of center circle
const BRANCH_DIST = 160; // distance from center to branch hub
const BRANCH_RX = 45; // branch hub radius X (ellipse)
const BRANCH_RY = 28; // branch hub radius Y (ellipse)
const LEAF_DIST = 80; // distance from branch hub to leaf
const LEAF_W = 80;
const LEAF_H = 32;
export function MindmapVisual({
  topic,
  branches = [],
  highlightCenter,
  showLeaves = true,
  label,
  mode = 'stimulus'
}: MindmapVisualProps) {
  const safeBranches = Array.isArray(branches) ? branches : [];
  const n = safeBranches.length;
  if (!topic && n === 0) {
    return <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500" data-id="element-13114">
        Mindmap kan niet worden getoond.
      </div>;
  }
  const isStimulus = mode === 'stimulus';
  // Compute SVG bounds — leave room for leaves on the outside
  const outerR = BRANCH_DIST + Math.max(BRANCH_RX, BRANCH_RY) + (showLeaves ? LEAF_DIST + LEAF_W / 2 : 0) + 12;
  const size = outerR * 2;
  const cx = outerR;
  const cy = outerR;
  // Distribute branches evenly. Start at -90° (top) for natural reading.
  const angleStep = n > 0 ? Math.PI * 2 / n : 0;
  const startAngle = -Math.PI / 2;
  return <figure className={`flex flex-col items-center gap-2 rounded-xl border-2 ${isStimulus ? 'border-violet-200 bg-violet-50/40' : 'border-violet-300 bg-gradient-to-br from-violet-50 to-pink-50 shadow-sm'} p-4`} aria-label={label ? `Mindmap: ${label}` : `Mindmap: ${topic ?? ''}`} data-id="element-13115">
      {label && <figcaption className="text-sm font-semibold text-violet-800" data-id="element-13116">
          {label}
        </figcaption>}

      <svg width="100%" viewBox={`0 0 ${size} ${size}`} style={{
      maxWidth: `${size}px`,
      height: 'auto'
    }} role="img" data-id="element-13117">
        {/* Branch lines (drawn first, behind everything) */}
        {safeBranches.map((branch, i) => {
        const angle = startAngle + i * angleStep;
        const bx = cx + Math.cos(angle) * BRANCH_DIST;
        const by = cy + Math.sin(angle) * BRANCH_DIST;
        const colors = BRANCH_PALETTE[i % BRANCH_PALETTE.length];
        return <motion.line key={`line-${i}`} x1={cx} y1={cy} x2={bx} y2={by} stroke={colors.stroke} strokeWidth={4} strokeDasharray={colors.dasharray} strokeLinecap="round" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          delay: 0.15 + i * 0.08,
          duration: 0.5
        }} data-id="element-13118" />;
      })}

        {/* Leaves (lines + pills) */}
        {showLeaves && safeBranches.map((branch, i) => {
        const angle = startAngle + i * angleStep;
        const bx = cx + Math.cos(angle) * BRANCH_DIST;
        const by = cy + Math.sin(angle) * BRANCH_DIST;
        const colors = BRANCH_PALETTE[i % BRANCH_PALETTE.length];
        const leaves = branch.leaves ?? [];
        if (leaves.length === 0) return null;
        // Distribute leaves in a small fan beyond the branch hub
        const fanSpread = Math.min(leaves.length - 1, 2) * 0.35;
        return leaves.map((leaf, j) => {
          const leafAngle = angle + (leaves.length === 1 ? 0 : (j / (leaves.length - 1) - 0.5) * fanSpread);
          const lx = bx + Math.cos(leafAngle) * LEAF_DIST;
          const ly = by + Math.sin(leafAngle) * LEAF_DIST;
          return <motion.g key={`leaf-${i}-${j}`} initial={{
            opacity: 0,
            scale: 0.6
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.5 + i * 0.08 + j * 0.05,
            duration: 0.25
          }} data-id="element-13119">
                  <line x1={bx} y1={by} x2={lx} y2={ly} stroke={colors.stroke} strokeWidth={1.5} strokeDasharray={colors.dasharray} opacity={0.5} data-id="element-13120" />
                  <rect x={lx - LEAF_W / 2} y={ly - LEAF_H / 2} width={LEAF_W} height={LEAF_H} rx={LEAF_H / 2} fill="white" stroke={colors.stroke} strokeWidth={1.5} strokeDasharray={colors.dasharray} data-id="element-13121" />
                  <foreignObject x={lx - LEAF_W / 2 + 4} y={ly - LEAF_H / 2 + 2} width={LEAF_W - 8} height={LEAF_H - 4} data-id="element-13122">
                    <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                fontSize: 10,
                fontWeight: 600,
                color: colors.text,
                lineHeight: 1.1,
                wordBreak: 'break-word'
              }} data-id="element-13123">
                      {leaf}
                    </div>
                  </foreignObject>
                </motion.g>;
        });
      })}

        {/* Branch hubs */}
        {safeBranches.map((branch, i) => {
        const angle = startAngle + i * angleStep;
        const bx = cx + Math.cos(angle) * BRANCH_DIST;
        const by = cy + Math.sin(angle) * BRANCH_DIST;
        const colors = BRANCH_PALETTE[i % BRANCH_PALETTE.length];
        const isHi = !!branch.highlight;
        return <motion.g key={`branch-${i}`} initial={{
          opacity: 0,
          scale: 0.6
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.4 + i * 0.08,
          type: 'spring',
          stiffness: 260,
          damping: 18
        }} data-id="element-13124">
              <ellipse cx={bx} cy={by} rx={BRANCH_RX} ry={BRANCH_RY} fill={isHi ? '#fde68a' : colors.fill} stroke={isHi ? '#f59e0b' : colors.stroke} strokeWidth={isHi ? 4 : 3} strokeDasharray={isHi ? 'none' : colors.dasharray} data-id="element-13125" />
              <foreignObject x={bx - BRANCH_RX + 4} y={by - BRANCH_RY + 4} width={BRANCH_RX * 2 - 8} height={BRANCH_RY * 2 - 8} data-id="element-13126">
                <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              fontSize: 12,
              fontWeight: 700,
              color: isHi ? '#78350f' : colors.text,
              lineHeight: 1.15,
              padding: '2px',
              wordBreak: 'break-word'
            }} data-id="element-13127">
                  {branch.label}
                </div>
              </foreignObject>
            </motion.g>;
      })}

        {/* Center node (rendered last so it's on top) */}
        <motion.g initial={{
        opacity: 0,
        scale: 0.5
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        type: 'spring',
        stiffness: 250,
        damping: 18
      }} data-id="element-13128">
          <circle cx={cx} cy={cy} r={CENTER_R} fill={highlightCenter ? '#fde68a' : '#7c3aed'} stroke={highlightCenter ? '#f59e0b' : '#5b21b6'} strokeWidth={highlightCenter ? 4 : 3} data-id="element-13129" />
          <foreignObject x={cx - CENTER_R + 6} y={cy - CENTER_R + 6} width={CENTER_R * 2 - 12} height={CENTER_R * 2 - 12} data-id="element-13130">
            <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 800,
            color: highlightCenter ? '#78350f' : '#ffffff',
            lineHeight: 1.15,
            padding: '4px'
          }} data-id="element-13131">
              {topic ?? '?'}
            </div>
          </foreignObject>
        </motion.g>
      </svg>

      {!isStimulus && <p className="mt-1 text-xs text-violet-700" data-id="element-13132">
          Midden = hoofdonderwerp · gekleurde hubs = hoofdtakken · pillen =
          details
        </p>}
    </figure>;
}
export default MindmapVisual;